import { css } from '@emotion/react';
import { Chips } from '@frontend/chips';
import { formatDate } from '@frontend/date';
import { CardBrand } from '@frontend/payments-card-brand';
import { Avatar, Chip, TableColumnConfig } from '@frontend/design-system';
import { SearchFailedPaymentsModel } from '../../../hooks/payment-failed/types';
import {
  getFailedTransactionsOriginLabel,
  mapFailedPaymentsTypeToPaymentType,
} from '../../../hooks/payment-failed/utils';
import { formatCentsToCurrency } from '../../../utils/currency-helpers';
import { getPaymentMethodBrand, GetLocationName } from '../../../utils/helpers';
import { PaymentsListContactName } from '../../PaymentsContactName/payments-list-contact-name';
import { StatusChip } from './status-chip';

export const generateColumns = (
  getLocationName: GetLocationName,
  showLocationName: boolean,
  forPrint = false
): TableColumnConfig<SearchFailedPaymentsModel>[] => [
  {
    id: 'location',
    Header: 'Location Name',
    accessor: (row) => row.locationId,
    cellRenderer: (locationId: string) =>
      forPrint ? (
        getLocationName(locationId ?? '')
      ) : (
        <Chips.LocationChip
          css={css`
            max-width: none;
          `}
        >
          {getLocationName(locationId ?? '')}
        </Chips.LocationChip>
      ),
    disableSortBy: true,
    omit: !showLocationName,
  },
  {
    id: 'customerName',
    Header: 'Contact Name',
    accessor: (row) => row,
    minWidth: 150,
    maxWidth: 170,
    cellRenderer: (row: SearchFailedPaymentsModel) => (
      <PaymentsListContactName
        id={row.personId}
        locationId={row.locationId}
        firstName={row.personFirstName}
        lastName={row.personLastName}
      />
    ),
  },
  {
    id: 'paidAmount',
    Header: 'Payment Amount',
    accessor: (row) => (row.amount ? formatCentsToCurrency(+row.amount) : ''),
    minWidth: 120,
    maxWidth: 150,
    disableResizing: true,
    disableSortBy: true,
    cellAlign: 'right',
  },
  {
    id: 'submittedAt',
    Header: 'Date / Time',
    maxWidth: 150,
    accessor: (row) => {
      return row.submittedAt ? formatDate(row.submittedAt, 'MMM D, YYYY h:mm a') : '';
    },
    disableResizing: true,
  },
  {
    id: 'paymentOrigin',
    Header: 'Payment Origin',
    accessor: (row) => {
      return getFailedTransactionsOriginLabel(row.origin);
    },
    minWidth: 64,
    maxWidth: 120,
    disableResizing: true,
    disableSortBy: true,
  },
  {
    id: 'paymentMethod',
    Header: 'Payment Method',
    accessor: (row) => row,
    cellRenderer: (row: SearchFailedPaymentsModel) => {
      const last4 = row.cardLastFour;

      return (
        <CardBrand
          brand={getPaymentMethodBrand({
            paymentType: mapFailedPaymentsTypeToPaymentType(row.paymentType),
            brand: row.cardBrand,
            bankName: row.bankName,
          })}
          last4={last4}
        />
      );
    },
    maxWidth: 180,
    disableSortBy: true,
  },
  {
    id: 'status',
    Header: 'Status',
    accessor: (row) => row,
    cellRenderer: (row: SearchFailedPaymentsModel) => <StatusChip status={row.paymentStatus} />,
    width: 100,
    disableSortBy: true,
  },
  {
    id: 'reasonForFailure',
    Header: 'Reason for Failure',
    accessor: (row) => row.paymentStatusReason,
    disableSortBy: true,
  },
  {
    id: 'requestId',
    Header: 'Request ID',
    accessor: (row) => row.paymentId,
    disableSortBy: true,
  },
  {
    id: 'officeUser',
    Header: 'Office User',
    accessor: (row) => row.userName,
    cellRenderer: (userName: string) =>
      !!userName ? <Chip.Person avatar={<Avatar name={userName} size='xxs' />}>{userName}</Chip.Person> : '',
    disableSortBy: true,
  },
];
